import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './TraineeList.css';
import '../ProgramStat/ProgramStat.css';
import { supabase } from '../../../lib/supabaseClient';
import { DailyHours, StudentProfile, Location } from '../../../lib/database.types';
import { useAuth } from '../../../lib/AuthProvider';
import WeeklyStudentsPanel from './WeeklyStudentsPanel';
import { FaTimes, FaEdit, FaSave, FaCheck, FaBan, FaMapMarkerAlt } from 'react-icons/fa';

interface WeeklyAttendanceSummary {
  id: string;
  student_id: string;
  week_number: number;
  scheduled_days: number;
  days_present: number;
  days_absent: number;
  noclass_days: number;
  avg_hours_per_day: number;
  late_minutes: number;
  created_at: string;
  updated_at: string;
}

interface WeeklyTrainee {
  firstName: string;
  lastName: string;
  department: string;
  weeklyHours?: number;
  status: 'At Risk' | 'Not At Risk' | 'N/A';
  riskScore?: number;
  attendanceRate?: number;
  absentRate?: number;
  holidayRatio?: number;
  weekNumber?: number;
}

interface GeneralTrainee {
  firstName: string;
  lastName: string;
  index: number;
  department: string;
  totalHours: number;
  requiredHours: number;
  progressRate: number;
  studentId: string;
  supervisor: string;
  userId: string;
}

interface PendingLocation extends Location {
  student_name?: string;
  student_id_number?: string;
}

interface TraineeProfile {
  firstName: string;
  lastName: string;
  department: string;
  studentId: string;
  supervisor: string;
  totalProgress: number;
  hoursCompleted: number;
  reportsSubmitted: number;
  requirementsCompleted: number;
  lastActivity: string;
  hoursToday: number;
  recentActivities: {
    timeIn: string;
    timeOut: string;
    date: string;
  }[];
  totalHoursCompleted: number;
  amHours: number;
  pmHours: number;
}

const TraineeList: React.FC = () => {
  const location = useLocation();
  
  // Determine initial tab from URL parameter
  const getInitialTab = (): 'weekly' | 'general' | 'department' | 'location' => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam === 'department') {
      return 'department';
    }
    if (tabParam === 'location') {
      return 'location';
    }
    return 'weekly';
  };
  
  const [activeTab, setActiveTab] = useState<'weekly' | 'general' | 'department' | 'location'>(getInitialTab());
  const [showModal, setShowModal] = useState(false);
  const [selectedTrainee, setSelectedTrainee] = useState<TraineeProfile | null>(null);
  const [loading, setLoading] = useState(true);
  
  const [weeklyTrainees, setWeeklyTrainees] = useState<WeeklyTrainee[]>([]);
  const [generalTrainees, setGeneralTrainees] = useState<GeneralTrainee[]>([]);
  
  // Department management state
  const [allStudents, setAllStudents] = useState<StudentProfile[]>([]);
  const [editingStudentId, setEditingStudentId] = useState<string | null>(null);
  const [editingDepartment, setEditingDepartment] = useState<string>('');
  const [editingSupervisor, setEditingSupervisor] = useState<string>('');
  const [offices, setOffices] = useState<string[]>([]);
  const [officesLoading, setOfficesLoading] = useState(false);
  
  // Location management state
  const [pendingLocations, setPendingLocations] = useState<PendingLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  
  // Get current user from AuthProvider to exclude from student profile fetch
  const { user } = useAuth();
  
  // Update active tab when URL changes
  useEffect(() => {
    const tab = getInitialTab();
    setActiveTab(tab);
  }, [location.search]);

  // Function to call the batch risk prediction API
  const predictRiskBatch = async (batchData: Array<{student_id: string, week_number: number, weekly_data: WeeklyAttendanceSummary}>): Promise<Map<string, {risk_label: string, attendance_rate: number, absent_rate: number, holiday_ratio: number}>> => {
    try {
      const response = await fetch('http://localhost:5000/predict_batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          batch: batchData.map(item => ({
            student_id: item.student_id,
            week_number: item.week_number,
            weekly_data: {
              scheduled_days: item.weekly_data.scheduled_days,
              days_present: item.weekly_data.days_present,
              days_absent: item.weekly_data.days_absent,
              noclass_days: item.weekly_data.noclass_days,
              avg_hours_per_day: item.weekly_data.avg_hours_per_day,
              late_minutes: item.weekly_data.late_minutes
            }
          }))
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const predictionsMap = new Map<string, {risk_label: string, attendance_rate: number, absent_rate: number, holiday_ratio: number}>();
      
      result.predictions.forEach((pred: any) => {
        const key = `${pred.student_id}_${pred.week_number}`;
        predictionsMap.set(key, {
          risk_label: pred.risk_label,
          attendance_rate: pred.attendance_rate,
          absent_rate: pred.absent_rate,
          holiday_ratio: pred.holiday_ratio
        });
      });
      
      return predictionsMap;
    } catch (error) {
      console.error('Error calling batch risk prediction API:', error);
      return new Map();
    }
  };

  // Fetch trainee data from database
  useEffect(() => {
    const fetchTraineeData = async () => {
      try {
        setLoading(true);

        // Fetch all data in parallel for better performance
        const [profilesResult, dailyHoursResult, weeklyAttendanceResult] = await Promise.all([
          supabase.from('student_profile').select('*'),
          supabase.from('daily_hours').select('*'),
          supabase.from('weekly_attendance_summary').select('*').order('week_number', { ascending: false })
        ]);

        const { data: profiles, error: profilesError } = profilesResult;
        const { data: dailyHours, error: hoursError } = dailyHoursResult;
        const { data: weeklyAttendance, error: weeklyError } = weeklyAttendanceResult;

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          setLoading(false);
          return;
        }

        if (!profiles || profiles.length === 0) {
          setGeneralTrainees([]);
          setWeeklyTrainees([]);
          setLoading(false);
          return;
        }

        if (hoursError) {
          console.error('Error fetching daily hours:', hoursError);
        }

        if (weeklyError) {
          console.error('Error fetching weekly attendance:', weeklyError);
        }

        // Calculate total hours for each student
        const studentHoursMap = new Map<string, number>();
        if (dailyHours) {
          dailyHours.forEach((hour: DailyHours) => {
            const currentTotal = studentHoursMap.get(hour.user_id) || 0;
            studentHoursMap.set(hour.user_id, currentTotal + hour.total_hours);
          });
        }

        // Create general trainees list
        const generalTraineesData: GeneralTrainee[] = profiles.map((profile: StudentProfile, index: number) => {
          const totalHours = studentHoursMap.get(profile.id) || 0;
          const requiredHours = 500; // Standard requirement
          const progressRate = Math.round((totalHours / requiredHours) * 100);

          return {
            firstName: profile.first_name,
            lastName: profile.last_name,
            index: index + 1,
            department: profile.department,
            totalHours: Math.round(totalHours * 100) / 100,
            requiredHours,
            progressRate: Math.min(progressRate, 100),
            studentId: profile.student_id,
            supervisor: profile.supervisor,
            userId: profile.id
          };
        });

        setGeneralTrainees(generalTraineesData);

        // Get all unique week numbers from weekly attendance data
        const allWeekNumbers = new Set<number>();
        if (weeklyAttendance && weeklyAttendance.length > 0) {
          weeklyAttendance.forEach((attendance: WeeklyAttendanceSummary) => {
            allWeekNumbers.add(attendance.week_number);
          });
        }
        const sortedWeeks = Array.from(allWeekNumbers).sort((a, b) => b - a);

        // Build batch data for all students with attendance data
        const batchData: Array<{student_id: string, week_number: number, weekly_data: WeeklyAttendanceSummary}> = [];
        
        for (const profile of (profiles as StudentProfile[])) {
          for (const weekNumber of sortedWeeks) {
            const studentWeeklyData = weeklyAttendance?.find(
              (attendance: WeeklyAttendanceSummary) => 
                attendance.student_id === profile.student_id && 
                attendance.week_number === weekNumber
            ) as WeeklyAttendanceSummary | undefined;

            if (studentWeeklyData) {
              batchData.push({
                student_id: profile.student_id,
                week_number: weekNumber,
                weekly_data: studentWeeklyData
              });
            }
          }
        }

        // Get all predictions in a single batch call
        const predictionsMap = batchData.length > 0 ? await predictRiskBatch(batchData) : new Map();

        // Build the weekly trainees list using the predictions
        const weeklyTraineesData: WeeklyTrainee[] = [];
        
        for (const profile of (profiles as StudentProfile[])) {
          for (const weekNumber of sortedWeeks) {
            const studentWeeklyData = weeklyAttendance?.find(
              (attendance: WeeklyAttendanceSummary) => 
                attendance.student_id === profile.student_id && 
                attendance.week_number === weekNumber
            ) as WeeklyAttendanceSummary | undefined;

            if (studentWeeklyData) {
              const key = `${profile.student_id}_${weekNumber}`;
              const riskPrediction = predictionsMap.get(key) || { risk_label: '0', attendance_rate: 0, absent_rate: 0, holiday_ratio: 0 };
              const weeklyHours = studentWeeklyData.avg_hours_per_day * studentWeeklyData.scheduled_days;
              
              weeklyTraineesData.push({
                firstName: profile.first_name,
                lastName: profile.last_name,
                department: profile.department,
                weeklyHours: Math.round(weeklyHours * 10) / 10,
                status: riskPrediction.risk_label === '1' ? 'At Risk' : 'Not At Risk',
                riskScore: parseFloat(riskPrediction.risk_label),
                attendanceRate: riskPrediction.attendance_rate,
                absentRate: riskPrediction.absent_rate,
                holidayRatio: riskPrediction.holiday_ratio,
                weekNumber: weekNumber
              });
            } else {
              weeklyTraineesData.push({
                firstName: profile.first_name,
                lastName: profile.last_name,
                department: profile.department,
                weeklyHours: undefined,
                status: 'N/A' as const,
                riskScore: undefined,
                attendanceRate: undefined,
                absentRate: undefined,
                holidayRatio: undefined,
                weekNumber: weekNumber
              });
            }
          }
        }

        setWeeklyTrainees(weeklyTraineesData);

      } catch (error) {
        console.error('Error fetching trainee data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTraineeData();
  }, [user?.id]);

  // Fetch department management data when department tab is active
  useEffect(() => {
    if (activeTab === 'department') {
      fetchAllStudents();
      fetchOffices();
    }
    if (activeTab === 'location') {
      fetchPendingLocations();
    }
  }, [activeTab]);

  // Fetch all students for department management
  const fetchAllStudents = async () => {
    try {
      const { data: studentsData, error } = await supabase
        .from('student_profile')
        .select('*')
        .order('last_name', { ascending: true });

      if (error) {
        console.error('Error fetching students:', error);
        return;
      }

      setAllStudents(studentsData || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  // Fetch offices from office_deployment table
  const fetchOffices = async () => {
    setOfficesLoading(true);
    try {
      const { data, error } = await supabase
        .from('office_deployment')
        .select('office')
        .order('office', { ascending: true });

      if (error) {
        console.error('Error fetching offices:', error);
        return;
      }

      // Extract unique office names
      if (data && data.length > 0) {
        const officeNames = data.map((item: any) => item.office).filter((office: any) => office);
        const uniqueOffices = [...new Set(officeNames)];
        setOffices(uniqueOffices);
      }
    } catch (error) {
      console.error('Error fetching offices:', error);
    } finally {
      setOfficesLoading(false);
    }
  };

  // Fetch pending locations for location management
  const fetchPendingLocations = async () => {
    setLocationsLoading(true);
    try {
      // Fetch all locations with pending status
      const { data: locationsData, error: locationsError } = await supabase
        .from('locations')
        .select('*')
        .eq('approval_status', 'pending')
        .order('created_at', { ascending: false });

      if (locationsError) {
        console.error('Error fetching pending locations:', locationsError);
        return;
      }

      if (!locationsData || locationsData.length === 0) {
        setPendingLocations([]);
        return;
      }

      // Get unique user IDs from locations
      const userIds = [...new Set(locationsData.map((loc: Location) => loc.user_id))];

      // Fetch student profiles for these users
      const { data: studentsData, error: studentsError } = await supabase
        .from('student_profile')
        .select('id, first_name, last_name, student_id')
        .in('id', userIds);

      if (studentsError) {
        console.error('Error fetching student profiles:', studentsError);
      }

      // Create a map of user_id to student info
      const studentMap = new Map<string, { name: string; studentId: string }>();
      if (studentsData) {
        studentsData.forEach((student: any) => {
          studentMap.set(student.id, {
            name: `${student.first_name} ${student.last_name}`,
            studentId: student.student_id
          });
        });
      }

      // Combine location data with student info
      const locationsWithStudents: PendingLocation[] = locationsData.map((loc: Location) => {
        const studentInfo = studentMap.get(loc.user_id);
        return {
          ...loc,
          student_name: studentInfo?.name || 'Unknown Student',
          student_id_number: studentInfo?.studentId || 'N/A'
        };
      });

      setPendingLocations(locationsWithStudents);
    } catch (error) {
      console.error('Error fetching pending locations:', error);
    } finally {
      setLocationsLoading(false);
    }
  };

  // Handle approving a location
  const handleApproveLocation = async (locationId: string) => {
    try {
      const { error } = await (supabase
        .from('locations') as any)
        .update({ approval_status: 'approved' })
        .eq('id', locationId);

      if (error) throw error;

      // Remove from pending list
      setPendingLocations(pendingLocations.filter(loc => loc.id !== locationId));
      alert('Location approved successfully!');
    } catch (error) {
      console.error('Error approving location:', error);
      alert('Failed to approve location. Please try again.');
    }
  };

  // Handle rejecting a location
  const handleRejectLocation = async (locationId: string) => {
    const confirmReject = window.confirm('Are you sure you want to reject this location request?');
    if (!confirmReject) return;

    try {
      const { error } = await (supabase
        .from('locations') as any)
        .update({ approval_status: 'rejected' })
        .eq('id', locationId);

      if (error) throw error;

      // Remove from pending list
      setPendingLocations(pendingLocations.filter(loc => loc.id !== locationId));
      alert('Location rejected.');
    } catch (error) {
      console.error('Error rejecting location:', error);
      alert('Failed to reject location. Please try again.');
    }
  };

  // Handle starting edit mode
  const handleEditDepartment = (student: StudentProfile) => {
    setEditingStudentId(student.id);
    setEditingDepartment(student.department || '');
    setEditingSupervisor(student.supervisor || '');
  };

  // Handle saving department and supervisor
  const handleSaveDepartment = async (student: StudentProfile) => {
    try {
      // Update student's department and supervisor in the database
      const { error }: any = await (supabase as any)
        .from('student_profile')
        .update({ 
          department: editingDepartment,
          supervisor: editingSupervisor 
        })
        .eq('id', student.id);

      if (error) throw error;

      // Update local state
      setAllStudents(allStudents.map(s => 
        s.id === student.id 
          ? { ...s, department: editingDepartment, supervisor: editingSupervisor }
          : s
      ));

      setEditingStudentId(null);
      setEditingDepartment('');
      setEditingSupervisor('');
      
      // Show success message
      alert('Department and supervisor updated successfully!');
    } catch (error) {
      console.error('Error saving department and supervisor:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditingStudentId(null);
    setEditingDepartment('');
    setEditingSupervisor('');
  };

  const viewTraineeDetails = async (trainee: WeeklyTrainee | GeneralTrainee) => {
    try {
      // Note: setDataFetching removed - new AuthProvider doesn't need role re-evaluation prevention
      // Get user ID from trainee data
      const userId = 'userId' in trainee ? trainee.userId : null;
      if (!userId) {
        console.error('User ID not found for trainee');
        return;
      }


      // Fetch detailed profile information
      const { data: profile, error: profileError } = await supabase
        .from('student_profile')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        return;
      }

      // Fetch daily hours for this student
      const { data: dailyHours, error: hoursError } = await supabase
        .from('daily_hours')
        .select('*')
        .eq('user_id', userId)
        .order('check_date', { ascending: false });

      if (hoursError) {
        console.error('Error fetching daily hours:', hoursError);
        return;
      }

      // Fetch recent check-ins for activity history
      const { data: checkIns, error: checkInsError } = await supabase
        .from('check_ins')
        .select('*')
        .eq('user_id', userId)
        .order('check_time', { ascending: false })
        .limit(5);

      if (checkInsError) {
        console.error('Error fetching check-ins:', checkInsError);
        return;
      }

      // Calculate total hours
      const totalHoursCompleted = dailyHours?.reduce((sum, day: DailyHours) => sum + day.total_hours, 0) || 0;
      
      // Get today's hours
      const today = new Date().toISOString().split('T')[0];
      const todayHours = dailyHours?.find((day: DailyHours) => day.check_date === today) as DailyHours | undefined;
      const hoursToday = todayHours?.total_hours || 0;

      // Get last activity date
      const lastActivity = (dailyHours as DailyHours[])?.[0]?.check_date || 'No activity';

      // Create recent activities from check-ins
      const recentActivities = checkIns?.map((checkIn: any) => {
        const checkTime = new Date(checkIn.check_time);
        const timeString = checkTime.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
        const dateString = checkTime.toLocaleDateString();
        
        return {
          timeIn: checkIn.check_type.includes('check_in') ? `AM ${timeString}` : `PM ${timeString}`,
          timeOut: checkIn.check_type.includes('check_out') ? `PM ${timeString}` : `AM ${timeString}`,
          date: dateString
        };
      }) || [];

      // Calculate progress
      const requiredHours = 500;
      const totalProgress = Math.round((totalHoursCompleted / requiredHours) * 100);

      const traineeProfile: TraineeProfile = {
        firstName: trainee.firstName,
        lastName: trainee.lastName,
        department: trainee.department,
        studentId: (profile as StudentProfile).student_id,
        supervisor: (profile as StudentProfile).supervisor,
        totalProgress: Math.min(totalProgress, 100),
        hoursCompleted: Math.round(totalHoursCompleted * 100) / 100,
        reportsSubmitted: 5, // Static as requested
        requirementsCompleted: 3, // Static as requested
        lastActivity: lastActivity,
        hoursToday: Math.round(hoursToday * 100) / 100,
        recentActivities: recentActivities.slice(0, 3), // Limit to 3 most recent
        totalHoursCompleted: Math.round(totalHoursCompleted * 100) / 100,
        amHours: todayHours?.am_hours || 0,
        pmHours: todayHours?.pm_hours || 0
      };
      
      setSelectedTrainee(traineeProfile);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching trainee details:', error);
    } finally {
      // Note: setDataFetching removed - new AuthProvider doesn't need role re-evaluation prevention
    }
  };

  // Close the modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedTrainee(null);
  };

  if (loading) {
    return (
      <div className="trainee-list">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading trainee data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trainee-list">
      <div className="tabs">
        <button 
          className={`tab-button ${activeTab === 'weekly' ? 'active' : ''}`}
          onClick={() => setActiveTab('weekly')}
        >
          Weekly
        </button>
        <button 
          className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General
        </button>
        <button 
          className={`tab-button ${activeTab === 'department' ? 'active' : ''}`}
          onClick={() => setActiveTab('department')}
        >
          Department Management
        </button>
        <button 
          className={`tab-button ${activeTab === 'location' ? 'active' : ''}`}
          onClick={() => setActiveTab('location')}
        >
          Location Management
        </button>
      </div>
      
      {activeTab === 'weekly' ? (
        <WeeklyStudentsPanel 
          trainees={weeklyTrainees} 
          loading={false}
          filterAtRisk={false}
        />
      ) : activeTab === 'general' ? (
        <div className="trainee-table general-view">
          {generalTrainees.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Department</th>
                  <th>Total Hours</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {generalTrainees.map((trainee, index) => (
                  <tr key={index}>
                    <td>{trainee.index}</td>
                    <td>{trainee.firstName}</td>
                    <td>{trainee.lastName}</td>
                    <td>{trainee.department}</td>
                    <td>
                      <div className="progress-container">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${trainee.progressRate}%` }} 
                          />
                        </div>
                        <div className="progress-text">
                          <span>Progress Rate: {trainee.progressRate}%</span>
                          <span>{trainee.totalHours}/{trainee.requiredHours} hours</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <button 
                        className="view-button" 
                        onClick={() => viewTraineeDetails(trainee)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-data-message">
              <h3>No trainee data available</h3>
              <p>No students have registered yet.</p>
              <p>Students need to register through the student registration system first.</p>
              <p>Once students register and start checking in/out, their data will appear here.</p>
            </div>
          )}
        </div>
      ) : activeTab === 'department' ? (
        <div className="department-management-section">
          <div className="department-header">
            <h3 className="department-title">All Students - Department & Supervisor Management</h3>
          </div>
          <div className="students-table-container">
            {allStudents.length > 0 ? (
              <table className="students-table">
                <thead>
                  <tr>
                    <th>Student ID</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Department</th>
                    <th>Supervisor</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allStudents.map((student) => (
                    <tr key={student.id}>
                      <td>{student.student_id}</td>
                      <td>{student.first_name}</td>
                      <td>{student.last_name}</td>
                      <td>
                        {editingStudentId === student.id ? (
                          <select
                            value={editingDepartment}
                            onChange={(e) => setEditingDepartment(e.target.value)}
                            className="department-select"
                            disabled={officesLoading}
                            autoFocus
                          >
                            <option value="">
                              {officesLoading ? 'Loading...' : 'Select department'}
                            </option>
                            {offices.map((office) => (
                              <option key={office} value={office}>
                                {office}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="department-badge">{student.department || 'N/A'}</span>
                        )}
                      </td>
                      <td>
                        {editingStudentId === student.id ? (
                          <input
                            type="text"
                            value={editingSupervisor}
                            onChange={(e) => setEditingSupervisor(e.target.value)}
                            className="supervisor-input"
                            placeholder="Enter supervisor name"
                          />
                        ) : (
                          <span>{student.supervisor || 'N/A'}</span>
                        )}
                      </td>
                      <td>
                        {editingStudentId === student.id ? (
                          <div className="action-buttons">
                            <button 
                              className="save-btn"
                              onClick={() => handleSaveDepartment(student)}
                              title="Save"
                            >
                              <FaSave />
                            </button>
                            <button 
                              className="cancel-btn"
                              onClick={handleCancelEdit}
                              title="Cancel"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ) : (
                          <button 
                            className="edit-btn"
                            onClick={() => handleEditDepartment(student)}
                            title="Edit Department & Supervisor"
                          >
                            <FaEdit />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data-message">
                <h3>No students found</h3>
                <p>No students have registered yet.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="location-management-section">
          <div className="location-header">
            <h3 className="location-title">
              <FaMapMarkerAlt style={{ marginRight: '8px' }} />
              Pending Location Requests
            </h3>
          </div>
          {locationsLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading pending locations...</p>
            </div>
          ) : pendingLocations.length > 0 ? (
            <div className="locations-table-container">
              <table className="locations-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Student ID</th>
                    <th>Location Name</th>
                    <th>Coordinates</th>
                    <th>Radius</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingLocations.map((location) => (
                    <tr key={location.id}>
                      <td>{location.student_name}</td>
                      <td>{location.student_id_number}</td>
                      <td>{location.name}</td>
                      <td className="coordinates-cell">
                        <span className="coord-label">Lat:</span> {location.latitude.toFixed(6)}<br/>
                        <span className="coord-label">Lng:</span> {location.longitude.toFixed(6)}
                      </td>
                      <td>{location.radius}m</td>
                      <td>{location.created_at ? new Date(location.created_at).toLocaleDateString() : 'N/A'}</td>
                      <td>
                        <div className="location-action-buttons">
                          <button 
                            className="approve-btn"
                            onClick={() => handleApproveLocation(location.id)}
                            title="Approve Location"
                          >
                            <FaCheck /> Approve
                          </button>
                          <button 
                            className="reject-btn"
                            onClick={() => handleRejectLocation(location.id)}
                            title="Reject Location"
                          >
                            <FaBan /> Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data-message">
              <FaMapMarkerAlt size={48} color="#ccc" />
              <h3>No Pending Location Requests</h3>
              <p>All location requests have been processed.</p>
              <p>New requests will appear here when students submit them.</p>
            </div>
          )}
        </div>
      )}
      
      {/* Trainee Profile Modal */}
      {showModal && selectedTrainee && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="trainee-profile">
              <div className="profile-header">
                <h2>Trainee Profile</h2>
                <button className="close-button" onClick={closeModal}>&times;</button>
              </div>
              
              <div className="profile-info-grid">
                <div className="info-field">
                  <label>First Name</label>
                  <div className="info-value">{selectedTrainee.firstName}</div>
                </div>
                <div className="info-field">
                  <label>Last Name</label>
                  <div className="info-value">{selectedTrainee.lastName}</div>
                </div>
                <div className="info-field">
                  <label>Supervisor</label>
                  <div className="info-value">{selectedTrainee.supervisor}</div>
                </div>
                <div className="info-field">
                  <label>Student ID</label>
                  <div className="info-value">{selectedTrainee.studentId}</div>
                </div>
                <div className="info-field">
                  <label>Department</label>
                  <div className="info-value">{selectedTrainee.department}</div>
                </div>
              </div>
              
              <div className="profile-stats">
                <div className="progress-circle">
                  <svg viewBox="0 0 36 36" className="progress-ring">
                    <path className="progress-ring-bg"
                      d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path className="progress-ring-fill"
                      strokeDasharray={`${selectedTrainee.totalProgress}, 100`}
                      d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <text x="18" y="20.35" className="progress-text">{selectedTrainee.totalProgress}%</text>
                  </svg>
                  <div className="progress-label">Total Progress</div>
                </div>
                
                <div className="stat-bars">
                  <div className="stat-item">
                    <label>Hours Completed</label>
                    <div className="stat-bar">
                      <div className="stat-bar-fill" style={{width: `${Math.min(selectedTrainee.totalProgress, 100)}%`}}></div>
                    </div>
                    <div className="stat-value">{selectedTrainee.totalHoursCompleted} / 500 hours</div>
                  </div>
                  <div className="stat-item">
                    <label>Reports Submitted</label>
                    <div className="stat-bar">
                      <div className="stat-bar-fill" style={{width: '75%'}}></div>
                    </div>
                    <div className="stat-value">{selectedTrainee.reportsSubmitted} reports</div>
                  </div>
                  <div className="stat-item">
                    <label>Requirements Completed</label>
                    <div className="stat-bar">
                      <div className="stat-bar-fill" style={{width: '40%'}}></div>
                    </div>
                    <div className="stat-value">{selectedTrainee.requirementsCompleted} requirements</div>
                  </div>
                </div>
                
                <div className="activity-metrics">
                  <div className="metric-box">
                    <span className="metric-label">Last Activity</span>
                    <span className="metric-value">{selectedTrainee.lastActivity}</span>
                  </div>
                  <div className="metric-box">
                    <span className="metric-label">Hours Today</span>
                    <span className="metric-value">{selectedTrainee.hoursToday} hours</span>
                  </div>
                  <div className="metric-box">
                    <span className="metric-label">AM Hours</span>
                    <span className="metric-value">{selectedTrainee.amHours} hours</span>
                  </div>
                  <div className="metric-box">
                    <span className="metric-label">PM Hours</span>
                    <span className="metric-value">{selectedTrainee.pmHours} hours</span>
                  </div>
                </div>
              </div>
              
              <div className="recent-activities">
                <h3>Recent Activities</h3>
                {selectedTrainee.recentActivities.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div className="time-record">
                      <div className="time-in">
                        <span className="time-label">Time In : {activity.timeIn.split(' ')[0]}</span>
                        <span className="time-value">{activity.timeIn.split(' ')[1]}</span>
                      </div>
                      <div className="time-out">
                        <span className="time-label">Time Out : {activity.timeOut.split(' ')[0]}</span>
                        <span className="time-value">{activity.timeOut.split(' ')[1]}</span>
                      </div>
                    </div>
                    <div className="activity-date">{activity.date}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TraineeList;
